import React, { useState, useEffect, useContext, useMemo } from "react";
import fetchHook from "../../hooks/fetchMSSQL-hook";
import { NavigationContext } from "../../context/navigation-context";
import formHook from "../../hooks/form-hook";
import ManufacturerForm from "../../components/Modals/ManufacturerForm";
import { NotificationManager } from "react-notifications";
import moment from "moment";
import SearchFilter from "../../components/Filter/SearchFilter";

export default function Manufacturers() {
  const [manufacturers, setManufacturers] = useState([]);
  const [windowHeight, setWindowHeight] = useState(window.innerHeight);

  const { fetchMssqlApi } = fetchHook();
  const { setHeaderData } = useContext(NavigationContext);
  const { form, openForm, closeForm } = formHook();

  const defaultFilter = {
    name: "manufacturers",
    page: 1,
    rowsPerPage: 50,
    manufacturerId: null,
    manufacturerName: "",
    code: "",
    isActive: "true",
    sortBy: "none",
  };

  const [filter, setFilter] = useState(defaultFilter);
  const [settingsFilter, setSettingsFilter] = useState({
    saveFilters: true,
    autoLoading: true,
    additionalFilter: true,
    flatView: false,
    rowsPerPage: 50,
  });

  const rowsPerPage = Number(settingsFilter.rowsPerPage || filter.rowsPerPage || 50);

  const parseThreeState = (val) => {
    if (val === "true") return true;
    if (val === "false") return false;
    return null;
  };

  const sortDefaults = {
    Name: "ASC",
    Code: "ASC",
    Country: "ASC",
    CreateDateTime: "DESC",
    IsActive: "DESC",
  };

  const getSortState = () => {
    const [field, dir] = String(filter.sortBy || "").split("|");
    return { field, dir: (dir || "").toUpperCase() };
  };

  const sortState = getSortState();

  const arrow = (field) => {
    if (sortState.field !== field) return "";
    return sortState.dir === "ASC" ? " ▲" : " ▼";
  };

  const getComparableValue = (row, field) => {
    if (field === "CreateDateTime") {
      return new Date(row?.CreateDateTime || 0).getTime();
    }

    if (field === "IsActive") {
      return Number(!!row?.IsActive);
    }

    return String(row?.[field] ?? "").toLowerCase();
  };

  const compareManufacturers = (a, b, field, dir) => {
    const aVal = getComparableValue(a, field);
    const bVal = getComparableValue(b, field);

    let result = 0;

    if (typeof aVal === "string" && typeof bVal === "string") {
      result = aVal.localeCompare(bVal, "pl", { sensitivity: "base" });
    } else if (aVal > bVal) {
      result = 1;
    } else if (aVal < bVal) {
      result = -1;
    }

    if (result === 0) {
      result = String(a?.Name ?? "").localeCompare(String(b?.Name ?? ""), "pl", {
        sensitivity: "base",
      });
    }

    return dir === "DESC" ? -result : result;
  };

  const getManufacturers = () => {
    fetchMssqlApi("manufacturers", {}, (res) => setManufacturers(res || []));
  };

  const handleHeaderSort = (field) => {
    const [curField, curDirRaw] = String(filter.sortBy || "").split("|");
    const curDir = (curDirRaw || "").toUpperCase();

    let nextDir;
    if (curField === field) {
      nextDir = curDir === "ASC" ? "DESC" : "ASC";
    } else {
      nextDir = sortDefaults[field] || "ASC";
    }

    const next = { ...filter, sortBy: `${field}|${nextDir}`, page: 1 };
    setFilter(next);
    getManufacturers(next);
  };

  useEffect(() => {
    const handleResize = () => {
      setWindowHeight(window.innerHeight);

      const screenHeight = window.innerHeight;
      const reservedSpace = 350;
      const visibleRows = Math.floor((screenHeight - reservedSpace) / 40);

      setFilter((prev) => ({
        ...prev,
        rowsPerPage: visibleRows > 50 ? 50 : Math.max(10, visibleRows),
      }));
    };

    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleSubmit = (evt, data, setData) => {
    evt.preventDefault();

    const submitter = evt.nativeEvent?.submitter;
    const fromToggle = submitter?.id === "isActive";

    if (fromToggle) {
      const current = manufacturers.find((m) => m.ManufacturerId === form);
      const nextIsActive = !Boolean(current?.IsActive);

      return fetchMssqlApi(
        `manufacturer/${form}`,
        { method: "PUT", data: { ...current, IsActive: nextIsActive } },
        () => {
          getManufacturers(filter);
          closeForm();
        }
      );
    }

    const isAvailable =
      manufacturers
        .filter((m) => m.ManufacturerId !== form)
        .map((m) => m.Code)
        .indexOf((data.Code || "").trim()) === -1;

    if (!isAvailable) {
      setData((prev) => ({ ...prev, Code: "" }));
      NotificationManager.error("Taki kod już istnieje");
      return;
    }

    if (!data.Name) {
      NotificationManager.error("Nazwa jest wymagana");
      return;
    }

    if (!/^[A-Z]{3}$/.test((data.Code || "").trim())) {
      NotificationManager.error("Kod musi mieć dokładnie 3 wielkie litery");
      return;
    }

    if (!data.Country) {
      NotificationManager.error("Kraj jest wymagany");
      return;
    }

    const payload = {
      Name: data.Name,
      Code: (data.Code || "").trim(),
      Country: data.Country,
      IsActive: Boolean(data.IsActive),
    };

    if (form === "new") {
      fetchMssqlApi(
        "manufacturer",
        { method: "POST", data: payload },
        () => {
          getManufacturers(filter);
          closeForm();
        }
      );
    } else {
      fetchMssqlApi(
        `manufacturer/${form}`,
        { method: "PUT", data: payload },
        () => {
          getManufacturers(filter);
          closeForm();
        }
      );
    }
  };

  useEffect(() => {
    setHeaderData({ text: "Producenci" });
    getManufacturers(defaultFilter);
  }, []);

  const filtered = useMemo(() => {
    const isActiveFilter = parseThreeState(filter.isActive);

    const data = (manufacturers || []).filter((t) => {
      const name = String(t?.Name ?? "");
      const code = String(t?.Code ?? "");
      const active = Boolean(t?.IsActive);

      if (filter.manufacturerName && !name.toLowerCase().includes(filter.manufacturerName.toLowerCase()))
        return false;

      if (filter.code && !code.toLowerCase().includes(filter.code.toLowerCase()))
        return false;

      if (isActiveFilter !== null && active !== isActiveFilter)
        return false;

      return true;
    });

    if (sortState.field && sortState.field !== "none") {
      data.sort((a, b) => compareManufacturers(a, b, sortState.field, sortState.dir));
    }

    return data;
  }, [manufacturers, filter.manufacturerName, filter.code, filter.isActive, filter.sortBy]);

  return (
    <div>
      <SearchFilter
        filterName="manufacturers"
        defaultFilter={defaultFilter}
        onFilterChange={(f) => setFilter((prev) => ({ ...prev, ...f }))}
        onSettingsChange={(s) => setSettingsFilter((prev) => ({ ...prev, ...s }))}
        search={getManufacturers}
        loading={false}
        totalCount={filtered.slice(
          (filter.page - 1) * rowsPerPage,
          filter.page * rowsPerPage
        ).length}
        dataList={manufacturers}
        forNow
      />

      <div className="card">
        <div className="card-header">
          <div className="d-flex justify-content-between">
            <div />
            <div onClick={openForm()} className="btn text-nowrap btn-outline-secondary mt-1 mb-1">
              <i className="fas fa-plus" />
            </div>
          </div>
        </div>

        <div
          className="overflow-auto custom-scroll"
          style={{
            maxHeight: `${windowHeight - 214}px`,
            overflowY: "auto",
          }}
        >
          {manufacturers.length ? (
            <table className="table table-striped">
              <thead>
                <tr className="headers-color text-center">
                  <th
                    style={{ cursor: "pointer", userSelect: "none" }}
                    onClick={() => handleHeaderSort("Name")}
                  >
                    Nazwa{arrow("Name")}
                  </th>
                  <th
                    style={{ cursor: "pointer", userSelect: "none" }}
                    onClick={() => handleHeaderSort("Code")}
                  >
                    Kod{arrow("Code")}
                  </th>
                  <th
                    style={{ cursor: "pointer", userSelect: "none" }}
                    onClick={() => handleHeaderSort("Country")}
                  >
                    Kraj{arrow("Country")}
                  </th>
                  <th
                    style={{ cursor: "pointer", userSelect: "none" }}
                    onClick={() => handleHeaderSort("CreateDateTime")}
                  >
                    Data utworzenia{arrow("CreateDateTime")}
                  </th>
                  <th
                    style={{ cursor: "pointer", userSelect: "none" }}
                    onClick={() => handleHeaderSort("IsActive")}
                  >
                    Aktywny{arrow("IsActive")}
                  </th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {filtered.slice(
                  (filter.page - 1) * rowsPerPage,
                  filter.page * rowsPerPage
                ).map((manufacturer) => (
                  <tr key={manufacturer.ManufacturerId}>
                    <td className="text-center">{manufacturer.Name}</td>
                    <td className="text-center">{manufacturer.Code}</td>
                    <td className="text-center">{manufacturer.Country}</td>
                    <td className="text-center">
                      {moment(manufacturer.CreateDateTime).format("DD.MM.YYYY HH:mm")}
                    </td>
                    <td className="text-center">
                      <form onSubmit={(evt) => handleSubmit(evt, {}, () => {})}>
                        <input type="checkbox" checked={!!manufacturer.IsActive} readOnly style={{ pointerEvents: "none" }} />
                      </form>
                    </td>
                    <td className="text-center">
                      {!!manufacturer.IsEditable && (
                        <button
                          onClick={openForm(manufacturer.ManufacturerId)}
                          className="btn btn-outline-primary btn-sm"
                        >
                          Edytuj
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="h3 d-flex justify-content-center align-items-center">
              Brak danych
            </div>
          )}
        </div>
      </div>

      {form && (
        <ManufacturerForm
          handleClose={closeForm}
          handleSubmit={handleSubmit}
          manufacturerData={manufacturers.find((m) => m.ManufacturerId === form)}
        />
      )}
    </div>
  );
}